<template>
  <q-dialog :model-value="modelValue" aria-labelledby="tax-xml-title" @update:model-value="$emit('update:modelValue', $event)" @hide="cancelRequest">
    <q-card class="tax-xml-dialog" data-testid="tax-xml-dialog">
      <q-card-section class="tax-xml-dialog__heading">
        <div>
          <h2 id="tax-xml-title">XML для налоговой</h2>
          <p>{{ reportName }}</p>
        </div>
        <q-btn flat round dense icon="close" aria-label="Закрыть экспорт XML" @click="close" />
      </q-card-section>
      <q-separator />
      <q-form aria-labelledby="tax-xml-title" @submit.prevent="download">
        <q-card-section class="tax-xml-dialog__body">
          <p class="tax-xml-dialog__intro">Проверьте реквизиты перед выгрузкой. Файл формируется и проверяется на сервере. Подписание и отправка выполняются отдельно.</p>
          <p v-if="isDeclaration" class="tax-xml-dialog__intro">Декларация с книгами скачивается архивом ZIP. Распакуйте его перед загрузкой: основной XML ссылается на файлы разделов 8 и 9.</p>
          <div class="tax-xml-dialog__fields">
            <q-input v-model="filing.tax_authority_code" outlined dense label="Код налоговой инспекции" inputmode="numeric" maxlength="4" :disable="loading" :error="Boolean(errorFor('tax_authority_code'))" :error-message="errorFor('tax_authority_code')" data-filing-field="tax_authority_code" />
            <q-input v-if="isDeclaration" v-model="filing.oktmo" outlined dense label="ОКТМО" inputmode="numeric" maxlength="11" :disable="loading" :error="Boolean(errorFor('oktmo'))" :error-message="errorFor('oktmo')" data-filing-field="oktmo" />
            <q-select v-if="isDeclaration" v-model="filing.taxpayer_location_code" outlined dense emit-value map-options :options="locationOptions" label="Место представления" :disable="loading" :error="Boolean(errorFor('taxpayer_location_code'))" :error-message="errorFor('taxpayer_location_code')" data-filing-field="taxpayer_location_code" />
            <q-input v-if="isDeclaration" v-model.number="filing.correction_number" outlined dense readonly type="number" min="0" max="0" step="1" label="Номер корректировки" hint="0 — первичная декларация. Уточнённые декларации пока не поддерживаются" :disable="loading" :error="Boolean(errorFor('correction_number'))" :error-message="errorFor('correction_number')" data-filing-field="correction_number" />
            <q-input v-model="filing.signer.last_name" outlined dense label="Фамилия подписанта" :disable="loading" :error="Boolean(errorFor('signer.last_name'))" :error-message="errorFor('signer.last_name')" data-filing-field="signer.last_name" />
            <q-input v-model="filing.signer.first_name" outlined dense label="Имя подписанта" :disable="loading" :error="Boolean(errorFor('signer.first_name'))" :error-message="errorFor('signer.first_name')" data-filing-field="signer.first_name" />
            <q-input v-model="filing.signer.middle_name" outlined dense label="Отчество подписанта, если есть" :disable="loading" :error="Boolean(errorFor('signer.middle_name'))" :error-message="errorFor('signer.middle_name')" data-filing-field="signer.middle_name" />
            <q-input v-model="filing.phone" outlined dense type="tel" label="Телефон, необязательно" :disable="loading" :error="Boolean(errorFor('phone'))" :error-message="errorFor('phone')" data-filing-field="phone" />
            <q-input v-if="isIpBook" v-model="filing.ogrnip" outlined dense label="ОГРНИП" inputmode="numeric" maxlength="15" :disable="loading" :error="Boolean(errorFor('ogrnip'))" :error-message="errorFor('ogrnip')" data-filing-field="ogrnip" />
            <q-input v-if="isIpBook" v-model="filing.ogrnip_date" outlined dense type="date" label="Дата записи в ЕГРИП" :disable="loading" :error="Boolean(errorFor('ogrnip_date'))" :error-message="errorFor('ogrnip_date')" data-filing-field="ogrnip_date" />
          </div>
          <div v-if="isDeclaration" class="tax-xml-dialog__scope">
            <p>Экспорт поддерживает разделы 1, 3, 8 и 9. Если вашим операциям нужны другие разделы, этот файл не заменяет полную декларацию.</p>
            <q-checkbox v-model="filing.declaration_scope_confirmed" label="Проверил: для моих операций достаточно разделов 1, 3, 8 и 9. Налогоплательщик не является стороной СЗПК." :disable="loading" data-testid="tax-xml-scope-confirmed" />
            <p v-if="errorFor('declaration_scope_confirmed')" class="text-negative" role="alert">{{ errorFor('declaration_scope_confirmed') }}</p>
          </div>
          <div v-if="message" class="tax-xml-dialog__status" :class="status === 'error' ? 'text-negative' : 'text-positive'" role="status" data-testid="tax-xml-status">
            {{ message }}
          </div>
          <ul v-if="diagnostics.length" class="tax-xml-dialog__diagnostics" data-testid="tax-xml-diagnostics">
            <li v-for="(item, index) in diagnostics" :key="index">
              <button v-if="diagnosticField(item)" class="tax-xml-dialog__diagnostic-link" type="button" @click="focusDiagnostic(item)">{{ item.message || item.code }}</button>
              <span v-else>{{ item.message || item.code }}</span>
              <small v-if="item.lineIndex !== null && item.lineIndex !== undefined"> · строка {{ item.lineIndex + 1 }}</small>
              <router-link v-if="documentRoute(item)" :to="documentRoute(item)" @click="close">Открыть документ</router-link>
              <small v-else-if="item.documentId"> · документ №{{ item.documentId }}</small>
            </li>
          </ul>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right" class="tax-xml-dialog__actions">
          <q-btn flat no-caps label="Закрыть" @click="close" />
          <q-btn type="submit" unelevated no-caps color="primary" label="Скачать XML ФНС" :loading="loading" :disable="disabled || regenerateRequired" data-testid="tax-xml-download" />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { requestTaxXmlArtifact, triggerBlobDownload } from '../../../../services/backendArtifacts'
import { documentInfoRoute } from '../../../../services/documentRegistry'

const emptyFiling = () => ({
  tax_authority_code: '', oktmo: '', correction_number: 0, taxpayer_location_code: '',
  signer: { last_name: '', first_name: '', middle_name: '' }, phone: '', declaration_scope_confirmed: false,
  ogrnip: '', ogrnip_date: ''
})

export default {
  name: 'TaxXmlExportDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    reportCode: { type: String, required: true },
    reportName: { type: String, required: true },
    payload: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    sourceDocuments: { type: Array, default: () => [] },
    organization: { type: Object, default: () => ({}) },
    reportRevision: { type: Number, default: 0 }
  },
  emits: ['update:modelValue', 'report-changed'],
  data() {
    return {
      filing: emptyFiling(), loading: false, status: '', message: '', diagnostics: [], fieldErrors: {},
      controller: null, revision: 0, regenerateRequired: false, ipFieldsRequested: false,
      locationOptions: [
        { label: '214 — по месту нахождения организации', value: '214' },
        { label: '116 — по месту жительства ИП', value: '116' }
      ]
    }
  },
  computed: {
    isDeclaration() { return this.reportCode === 'vat_declaration' },
    isIpBook() { return !this.isDeclaration && (this.ipFieldsRequested || /^\d{12}$/.test(String(this.organization.inn || ''))) },
    contextKey() {
      return JSON.stringify([this.payload.org_id, this.reportCode, this.payload.period_start, this.payload.period_end, this.payload.expected_source_hash, this.reportRevision])
    }
  },
  watch: {
    modelValue(value) { if (value) this.prefillOrganization() },
    contextKey() {
      this.close()
      this.filing = emptyFiling()
      this.clearFeedback()
      this.regenerateRequired = false
      this.ipFieldsRequested = false
    },
    disabled(value) { if (value) this.close() }
  },
  beforeUnmount() { this.cancelRequest() },
  methods: {
    prefillOrganization() {
      if (!this.filing.ogrnip) this.filing.ogrnip = String(this.organization.ogrnip || '')
      if (!this.filing.ogrnip_date) this.filing.ogrnip_date = String(this.organization.ogrnip_date || '')
    },
    clearFeedback() { this.message = ''; this.status = ''; this.fieldErrors = {}; this.diagnostics = [] },
    errorFor(field) { return this.fieldErrors[field] || '' },
    validate() {
      const errors = {}
      if (!/^\d{4}$/.test(String(this.filing.tax_authority_code).trim())) errors.tax_authority_code = 'Укажите код инспекции из 4 цифр'
      if (!this.filing.signer.last_name.trim()) errors['signer.last_name'] = 'Укажите фамилию подписанта'
      if (!this.filing.signer.first_name.trim()) errors['signer.first_name'] = 'Укажите имя подписанта'
      if (this.isIpBook) {
        if (!/^\d{15}$/.test(this.filing.ogrnip.trim())) errors.ogrnip = 'Укажите ОГРНИП из 15 цифр'
        const date = new Date(`${this.filing.ogrnip_date}T00:00:00Z`)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(this.filing.ogrnip_date) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== this.filing.ogrnip_date) errors.ogrnip_date = 'Укажите корректную дату записи в ЕГРИП'
      }
      if (this.isDeclaration) {
        if (!/^(?:\d{8}|\d{11})$/.test(String(this.filing.oktmo).trim())) errors.oktmo = 'Укажите ОКТМО из 8 или 11 цифр'
        if (!['214', '116'].includes(this.filing.taxpayer_location_code)) errors.taxpayer_location_code = 'Выберите место представления'
        if (this.filing.correction_number !== 0) errors.correction_number = 'Уточнённые декларации пока не поддерживаются'
        if (!this.filing.declaration_scope_confirmed) errors.declaration_scope_confirmed = 'Подтвердите состав разделов декларации'
      }
      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    normalizedFiling() {
      const filing = {
        tax_authority_code: this.filing.tax_authority_code.trim(),
        signer: Object.fromEntries(Object.entries(this.filing.signer).map(([key, value]) => [key, value.trim()])),
        phone: this.filing.phone.trim()
      }
      if (this.isDeclaration) Object.assign(filing, {
        oktmo: this.filing.oktmo.trim(), correction_number: this.filing.correction_number,
        taxpayer_location_code: this.filing.taxpayer_location_code, declaration_scope_confirmed: this.filing.declaration_scope_confirmed
      })
      if (this.isIpBook) Object.assign(filing, { ogrnip: this.filing.ogrnip.trim(), ogrnip_date: this.filing.ogrnip_date })
      return filing
    },
    cancelRequest() {
      this.revision += 1
      this.controller?.abort()
      this.controller = null
      this.loading = false
    },
    close() {
      this.cancelRequest()
      this.$emit('update:modelValue', false)
    },
    diagnosticField(item) {
      const path = String(item.fieldPath || '').replace(/^filing\./, '')
      return ['tax_authority_code', 'oktmo', 'taxpayer_location_code', 'correction_number', 'signer.last_name', 'signer.first_name', 'signer.middle_name', 'phone', 'declaration_scope_confirmed', 'ogrnip', 'ogrnip_date'].includes(path) ? path : ''
    },
    focusDiagnostic(item) {
      const field = this.diagnosticField(item)
      const root = this.$el?.querySelector ? this.$el : document.querySelector('[data-testid="tax-xml-dialog"]')
      const target = field === 'declaration_scope_confirmed'
        ? root?.querySelector('[data-testid="tax-xml-scope-confirmed"]')
        : root?.querySelector(`[data-filing-field="${field}"]`)
      const input = target?.querySelector('input, [tabindex]') || target
      target?.scrollIntoView({ block: 'nearest' })
      input?.focus()
    },
    documentRoute(item) {
      const id = item.documentId || item.document_id
      const source = this.sourceDocuments.find((document) => String(document.document_id || document.id) === String(id))
      return source ? documentInfoRoute({ ...source, id }) : null
    },
    async download() {
      if (this.disabled || this.loading || this.regenerateRequired) return
      this.clearFeedback()
      if (!this.validate()) {
        this.message = 'Проверьте реквизиты выгрузки'
        this.status = 'error'
        this.$nextTick(() => this.focusDiagnostic({ fieldPath: Object.keys(this.fieldErrors)[0] }))
        return
      }
      this.controller = new AbortController()
      const revision = ++this.revision
      this.loading = true
      try {
        const result = await requestTaxXmlArtifact({ connection: this.connection, payload: { ...this.payload, filing: this.normalizedFiling() }, signal: this.controller.signal })
        if (revision !== this.revision || this.disabled || !this.modelValue) return
        triggerBlobDownload(result.blob, result.metadata.filename)
        this.status = 'success'
        this.message = result.metadata.contentType.includes('zip')
          ? `Архив ${result.metadata.filename} скачан. Он содержит несколько файлов XML.`
          : `Файл ${result.metadata.filename} скачан.`
      } catch (error) {
        if (revision !== this.revision || error.name === 'AbortError') return
        this.status = 'error'
        this.message = error.message || 'Не удалось сформировать XML'
        this.diagnostics = Array.isArray(error.diagnostics) ? error.diagnostics : []
        this.diagnostics.forEach((item) => {
          const field = this.diagnosticField(item)
          if (field) this.fieldErrors[field] = item.message || item.code
          if (field === 'ogrnip' || field === 'ogrnip_date') this.ipFieldsRequested = true
        })
        if (error.code === 'report_changed' || error.status === 409) {
          this.regenerateRequired = true
          this.message = 'Данные отчёта изменились. Закройте окно и сформируйте отчёт заново.'
          this.$emit('report-changed')
        }
      } finally {
        if (revision === this.revision) { this.loading = false; this.controller = null }
      }
    }
  }
}
</script>

<style scoped>
.tax-xml-dialog { width: 720px; max-width: calc(100vw - 32px); }
.tax-xml-dialog__heading { position: sticky; top: 0; z-index: 2; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; background: var(--sid-color-surface-0); }
.tax-xml-dialog h2 { margin: 0; font-size: 20px; line-height: 1.4; }
.tax-xml-dialog__heading p { margin: 4px 0 0; color: var(--sid-color-text-secondary); }
.tax-xml-dialog__intro { margin: 0 0 20px; }
.tax-xml-dialog__fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.tax-xml-dialog__scope { margin-top: 20px; }
.tax-xml-dialog__scope p { margin: 0 0 8px; }
.tax-xml-dialog__status, .tax-xml-dialog__diagnostics { margin-top: 16px; }
.tax-xml-dialog__diagnostics { padding-left: 20px; }
.tax-xml-dialog__diagnostics li + li { margin-top: 8px; }
.tax-xml-dialog__diagnostic-link { padding: 0; border: 0; background: transparent; color: inherit; text-decoration: underline; cursor: pointer; text-align: left; font: inherit; }
.tax-xml-dialog__diagnostics a { display: block; }
@media (max-width: 599px) {
  .tax-xml-dialog { max-width: calc(100vw - 24px); }
  .tax-xml-dialog__fields { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .tax-xml-dialog__body { padding: 16px; }
  .tax-xml-dialog__actions { position: sticky; bottom: 0; background: var(--sid-color-surface-0); }
  .tax-xml-dialog__actions .q-btn { min-height: 44px; }
}
</style>
